import {
    ArrowBackIosNewRounded,
    ContentCopy,
    WhatsApp,
    Share,
    Receipt
} from "@mui/icons-material";
import {
    CircularProgress,
    IconButton,
    Button,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import { Box } from "@mui/system";
import { getAuth } from "firebase/auth";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AccountProgressBar from "../components/accountProgressBar";
import { IOSSwitch } from "../components/CustomButtons";
import DeleteButton from "../components/deleteButton";
import AddEntryForm from "../components/forms/addEntryForm";
import EditEntryForm from "../components/forms/editEntryForm";
import { generateWhatsAppLink } from "../utils/whatsappGenerator";
import {
    deleteAccount,
    getAccountDetails,
    setAccountStatus,
    getcustomerDetails
} from "../firebase/dataHandler";
import { useTranslation } from "react-i18next";

function AccountDetail() {
  const { t } = useTranslation();
  let { accountId } = useParams();
  const user = getAuth().currentUser;
  const uid = user.uid;
  accountId = `${accountId.split("_")[0]}_${accountId.split("_")[1]}_${uid}`;

  const getUnPaidDays = (date) =>
    Math.round(
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24)
    ) - 1;
  const getUnPaidWeeks = (date) =>
    Math.round(
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24 * 7)
    );
  const getUnPaidMonths = (date) => {
    const today = new Date();
    const d = new Date(date);
    return (today.getFullYear() - d.getFullYear()) * 12 + today.getMonth() - d.getMonth();
  };

  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [isLoading, setLoading] = useState(true);
  useEffect(() => {
    getDetails();
  }, [accountId]);

  const getDetails = () => {
    getAccountDetails(accountId)
      .then((accountData) => {
        setData(accountData);
        if (accountData?.clientId) {
          getcustomerDetails(accountData.clientId).then(custData => {
            setCustomer(custData);
          }).catch(console.error);
        }
        setLoading(false);
      })
      .catch((e) => {
        alert(e);
        console.error(e);
      });
  };

  const navigate = useNavigate();

  let paidDayCount =
    data && data.entries
      ? Object.values(data.entries).reduce((acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.dayCount)), 0)
      : 0;
  let paidAmount =
    data && data.entries
      ? Object.values(data.entries).reduce((acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.amountPaid)), 0)
      : 0;

  let hasUpfront = data && (data.collectUpfrontInterest || (data.entries && Object.values(data.entries).some(e => e.isUpfrontInterest)));
  let expectedTotal = data ? (data.installmentAmount * data.installmentPeriod) || (hasUpfront ? Number(data.amount) : (Number(data.amount) + Number(data.interestAmount || 0))) : 0;

  let isNotTallied = data ? paidAmount !== (paidDayCount * Number(data.installmentAmount)) : false;

  let unpaidDayCount = data
    ? data.installmentType === 'daily'
      ? getUnPaidDays(data.date)
      : data.installmentType === 'weekly'
      ? getUnPaidWeeks(data.date)
      : data.installmentType === 'monthly' ? getUnPaidMonths(data.date) : 0
    : 0;

  let installmentTypePhrase = data && (data.installmentType === "daily"
    ? "days"
    : data.installmentType === "weekly"
    ? "weeks"
    : data.installmentType === "monthly" && "months"
  );

  const consoleUrl = data?.clientId
    ? `https://finaculator.web.app/console/c/${data.clientId}`
    : null;

  return (
    <div style={{ position: 'relative', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {data ? (
        <>
          {isLoading && <CircularProgress style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }} />}

          {/* ── Sticky Top Bar ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-primary)', zIndex: 100, flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIosNewRounded />
              </IconButton>
              <div>
                <h2
                  style={{ margin: 0, fontSize: 15, fontWeight: 700, cursor: 'pointer', color: 'var(--text-primary)' }}
                  onClick={() => navigate(`/customers/${data.clientId.split("_")[0]}`)}
                >
                  {data.name}
                </h2>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>A/C {data.accountId.split('_')[0]}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <DeleteButton onPositive={() => { deleteAccount(data.accountId); navigate("/"); }} />
              <FormControlLabel
                control={
                  <IOSSwitch
                    name="Account Status"
                    checked={data.isActive}
                    onChange={(e) => {
                      setData({ ...data, isActive: e.target.checked });
                      setAccountStatus(data.accountId, e.target.checked);
                    }}
                    sx={{ m: 1 }}
                  />
                }
                labelPlacement="start"
                label={t('account_detail.account_status')}
              />
            </div>
          </div>

          {/* ── Badge Strip ── */}
          <div style={{ display: 'flex', gap: 6, padding: '6px 20px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-primary)', flexShrink: 0 }}>
            <div style={{ fontSize: 10, background: 'rgba(21, 195, 88, 0.1)', color: 'var(--accent-green)', padding: '2px 10px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(21, 195, 88, 0.2)', textTransform: 'uppercase' }}>
              {data.installmentType || 'daily'}
            </div>
            {/* OVERDUE tag */}
            {unpaidDayCount > data.installmentPeriod && (data.installmentPeriod - paidDayCount) > 0 && (
              <div style={{ fontSize: 10, background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '2px 10px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(239,68,68,0.35)', textTransform: 'uppercase' }}>
                🔴 {t('account_detail.overdue')}
              </div>
            )}
            {isNotTallied && (
              <div style={{ fontSize: 10, background: 'rgba(248,113,113,0.1)', color: '#f87171', padding: '2px 10px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(248,113,113,0.2)' }}>
                ⚠️ {t('account_detail.not_tallied')}
              </div>
            )}
          </div>

          {/* ── Master-Detail Body ── */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* Left: Ledger — Main Content (fills remaining width) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 120px' }}>

              {/* Summary Cards */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 120, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('account_detail.recovered')}</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0A9E5C' }}>₹{paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</p>
                </div>
                <div style={{ flex: 1, minWidth: 120, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('account_detail.outstanding')}</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#ef4444' }}>₹{(expectedTotal - paidAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</p>
                </div>
                <div style={{ flex: 1, minWidth: 120, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('account_detail.progress')}</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{paidDayCount} / {data.installmentPeriod}</p>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{installmentTypePhrase} {t('account_detail.paid')}</p>
                </div>
              </div>

              {/* Mobile-only: Status Alerts + Full Account Details */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>

                {/* Overdue / Arrears Alert */}
                {(() => {
                  const arrears = unpaidDayCount - paidDayCount;
                  const isOverdue = unpaidDayCount > data.installmentPeriod && (data.installmentPeriod - paidDayCount) > 0;
                  const overduePeriods = isOverdue ? unpaidDayCount - data.installmentPeriod : 0;
                  return (
                    <>
                      {isOverdue && (
                        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>🔴</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#ef4444' }}>{t('account_detail.overdue_by')} {overduePeriods} {installmentTypePhrase}</p>
                            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{t('account_detail.loan_period_passed')}</p>
                          </div>
                        </div>
                      )}
                      {arrears > 0 && !isOverdue && (
                        <div style={{ background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>⚠️</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#fb923c' }}>{arrears} {installmentTypePhrase} {t('account_detail.in_arrears')}</p>
                            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{t('account_detail.missed_payments')}</p>
                          </div>
                        </div>
                      )}
                      {isNotTallied && (
                        <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
                          <p style={{ margin: 0, fontSize: 12, color: '#f87171', fontWeight: 700 }}>⚠️ {t('account_detail.amount_mismatch')}</p>
                          <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                            {t('account_detail.expected')}: ₹{(paidDayCount * data.installmentAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })} · {t('account_detail.recovered')}: ₹{paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                          </p>
                        </div>
                      )}
                      {!isOverdue && arrears === 0 && paidDayCount < data.installmentPeriod && (
                        <div style={{ background: 'rgba(45,132,255,0.08)', border: '1px solid rgba(45,132,255,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>✅</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: 'var(--accent-green)' }}>{t('account_detail.on_track')}</p>
                            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{data.installmentPeriod - paidDayCount} {installmentTypePhrase} {t('account_detail.remaining')}</p>
                          </div>
                        </div>
                      )}
                      {paidDayCount >= data.installmentPeriod && (
                        <div style={{ background: 'rgba(10,158,92,0.08)', border: '1px solid rgba(10,158,92,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>🎉</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#0A9E5C' }}>{t('account_detail.fully_paid')}</p>
                            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>All {data.installmentPeriod} {installmentTypePhrase} {t('account_detail.all_completed')}</p>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Account Details Panel (mobile) */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '4px 16px', marginBottom: 16 }}>
                  <p style={{ margin: '12px 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{t('account_detail.account_details')}</p>
                  {[
                    { label: t('account_detail.ac_number'), value: data.accountId.split('_')[0] },
                    { label: t('account_detail.principal_amount'), value: `₹ ${data.amount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#0A9E5C' },
                    { label: t('account_detail.interest_payable'), value: `₹ ${data.interestAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}` },
                    { label: t('account_detail.equated_installment'), value: `₹ ${data.installmentAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}` },
                    { label: t('account_detail.total_installments'), value: `${data.installmentPeriod} ${installmentTypePhrase}` },
                    { label: t('account_detail.start_date'), value: new Date(data.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) },
                    { label: t('account_detail.total_recovered'), value: `₹ ${paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#0A9E5C' },
                    { label: t('account_detail.outstanding'), value: `₹ ${(expectedTotal - paidAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#ef4444' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>{label}</p>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</p>
                    </div>
                  ))}
                  <div style={{ height: 4 }} />
                </div>
              </Box>

              {/* Progress Bar */}
              <div style={{ marginBottom: 20 }}>
                <AccountProgressBar
                  key={data.accountId + "0"}
                  keyId={`progress_${data.accountId}`}
                  startDate={data.date}
                  period={data.installmentPeriod}
                  installmentType={data.installmentType || 'daily'}
                  values={[paidDayCount, unpaidDayCount]}
                />
              </div>

              {/* Status Alert — OVERDUE or ARREARS */}
              {(() => {
                const remainingInstallments = data.installmentPeriod - paidDayCount;
                const isOverdue = unpaidDayCount > data.installmentPeriod && remainingInstallments > 0;
                const overduePeriods = isOverdue ? unpaidDayCount - data.installmentPeriod : 0;
                const arrears = Math.min(remainingInstallments, Math.max(0, unpaidDayCount - paidDayCount));
                const hasArrears = !isOverdue && arrears > 3;

                const whatsappBtn = (unpaidDuration) => customer && customer.contact && (
                  <Button
                    variant="contained"
                    startIcon={<WhatsApp />}
                    onClick={() => {
                      const url = generateWhatsAppLink(customer.contact, {
                        customerName: data.name,
                        accountId: data.accountId.split('_')[0],
                        balance: data.amount - paidAmount,
                        unpaidDuration,
                        durationUnit: installmentTypePhrase,
                        consoleUrl,
                        financierName: user?.displayName || 'Financier'
                      });
                      window.open(url, '_blank');
                    }}
                    sx={{ backgroundColor: '#25D366', color: '#fff', textTransform: 'none', fontWeight: 'bold', borderRadius: 4, '&:hover': { backgroundColor: '#128C7E' } }}
                  >
                    {t('account_detail.send_whatsapp_reminder')}
                  </Button>
                );

                if (isOverdue) {
                  return (
                    <div style={{ backgroundColor: '#7f1d1d', textAlign: 'center', padding: '16px', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: 16, border: '1px solid rgba(239,68,68,0.5)' }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#fca5a5' }}>🔴 {t('account_detail.overdue_by')} {overduePeriods} {installmentTypePhrase.toUpperCase()}</p>
                      <p style={{ margin: 0, fontSize: 12, color: 'rgba(252,165,165,0.7)' }}>
                        Loan period of {data.installmentPeriod} {installmentTypePhrase} ended. {remainingInstallments} installment{remainingInstallments !== 1 ? 's' : ''} still unpaid.
                      </p>
                      {whatsappBtn(overduePeriods)}
                    </div>
                  );
                }

                if (hasArrears) {
                  return (
                    <div style={{ backgroundColor: '#7c2d12', textAlign: 'center', padding: '14px', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: 16, border: '1px solid rgba(251,146,60,0.4)' }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#fdba74' }}>⚠️ {arrears} {installmentTypePhrase} {t('account_detail.in_arrears')}</p>
                      <p style={{ margin: 0, fontSize: 12, color: 'rgba(253,186,116,0.7)' }}>{t('account_detail.missed_payments')}</p>
                      {whatsappBtn(arrears)}
                    </div>
                  );
                }

                return null;
              })()}

              {/* Share Statement / Notices */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                   component={Link}
                   to={`/invoice/v/${data.accountId}`}
                   target="_blank"
                   variant="outlined"
                   size="small"
                   startIcon={<Receipt />}
                   sx={{ borderColor: 'rgba(21, 195, 88, 0.5)', color: 'var(--accent-green)', textTransform: 'none', fontWeight: 600, borderRadius: 4 }}
                >
                  {t('account_detail.view_statement')}
                </Button>
                {unpaidDayCount > paidDayCount && (
                  <Button
                     component={Link}
                     to={`/invoice/v/${data.accountId}?type=overdue`}
                     target="_blank"
                     variant="outlined"
                     size="small"
                     startIcon={<Receipt />}
                     sx={{ borderColor: 'rgba(244,67,54,0.5)', color: '#f44336', textTransform: 'none', fontWeight: 600, borderRadius: 4 }}
                  >
                    {t('account_detail.view_overdue_notice')}
                  </Button>
                )}
                {customer && customer.contact && consoleUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Share />}
                    onClick={() => {
                      const url = generateWhatsAppLink(customer.contact, {
                        customerName: data.name,
                        accountId: data.accountId.split('_')[0],
                        balance: data.amount - paidAmount,
                        consoleUrl,
                        mode: 'statement',
                        financierName: user?.displayName || 'Financier'
                      });
                      window.open(url, '_blank');
                    }}
                    sx={{ borderColor: '#25D366', color: '#25D366', textTransform: 'none', fontWeight: 600, borderRadius: 4, '&:hover': { background: 'rgba(37,211,102,0.08)', borderColor: '#25D366' } }}
                  >
                    {t('account_detail.share_via_wa')}
                  </Button>
                )}
              </Box>

              {/* Background Seals */}
              {paidDayCount >= data.installmentPeriod && (
                <div style={{ position: 'fixed', bottom: '40px', left: '40px', transform: 'rotate(-15deg)', border: '6px solid #0A9E5C', color: '#0A9E5C', padding: '10px 30px', fontSize: 36, fontWeight: 900, letterSpacing: 6, opacity: 0.3, pointerEvents: 'none', zIndex: 9999 }}>
                  {t('account_detail.paid_off')}
                </div>
              )}
              {unpaidDayCount > data.installmentPeriod && (data.installmentPeriod - paidDayCount) > 0 && (
                <div style={{ position: 'fixed', bottom: '40px', left: '40px', transform: 'rotate(-15deg)', border: '6px solid #ef4444', color: '#ef4444', padding: '10px 30px', fontSize: 36, fontWeight: 900, letterSpacing: 6, opacity: 0.3, pointerEvents: 'none', zIndex: 9999 }}>
                  {t('account_detail.overdue')}
                </div>
              )}
              
              {/* Ledger Header */}
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{t('account_detail.ledger')}</h2>
                  <div style={{ background: 'rgba(10,158,92,0.1)', color: '#0A9E5C', padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: 12 }}>
                    {data.entries ? Object.keys(data.entries).length : 0} {t('account_detail.entries')}
                  </div>
                </div>
              </div>

              {/* Ledger Entries */}
              {data.entries ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.keys(data.entries)
                    .sort((a, b) => new Date(data.entries[b].date) - new Date(data.entries[a].date))
                    .map((entry, i) => (
                      <div
                        key={i}
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 14,
                          padding: '12px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                            {data.entries[entry].date.split("-").reverse().join("-")}
                          </div>
                          {data.entries[entry].isUpfrontInterest ? (
                            <div style={{ fontSize: 12, color: '#fb923c', fontWeight: 600 }}>
                              {t('account_detail.upfront_interest')}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {data.entries[entry].dayCount} {installmentTypePhrase}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#0A9E5C' }}>
                            ₹{data.entries[entry].amountPaid.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                          </div>
                          
                              <IconButton
                                component={Link}
                                to={`/invoice/v/${accountId}/${entry}`}
                                target="_blank"
                                size="small"
                                style={{ color: 'var(--accent-green)', background: 'rgba(21, 195, 88, 0.1)' }}
                              >
                                <Receipt fontSize="small" />
                              </IconButton>
                          {data.entries[entry].isUpfrontInterest ? (
                            <DeleteButton
                              style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)' }}
                              alertMessage="Delete this only if you didn't actually collect the upfront interest."
                              onPositive={() => {
                                import('../firebase/dataHandler').then(({ deleteEntry }) => {
                                  deleteEntry(accountId, entry).then(() => getDetails()).catch(e => alert(e));
                                });
                              }}
                            />
                          ) : (
                            <>
                              <EditEntryForm
                                data={{
                                  ...data.entries[entry],
                                  amountPaid: data.entries[entry].amountPaid / data.entries[entry].dayCount,
                                }}
                                accountId={accountId}
                                entryId={entry}
                                refresh={getDetails}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 40 }}>
                  No entries yet. Add the first entry!
                </p>
              )}
            </div>

            {/* Right: Properties Pane — sticky side panel (desktop only) */}
            <Box sx={{
              width: 280,
              flexShrink: 0,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              overflowY: 'auto',
              borderLeft: '1px solid var(--border-subtle)',
              background: 'var(--bg-card)',
              padding: '20px 18px',
            }}>
              <p style={{ margin: '0 0 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                Account Details
              </p>

              {[
                { label: 'A/C Number', value: data.accountId.split('_')[0], copyable: true },
                { label: 'Principal Amount', value: `₹ ${data.amount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#0A9E5C' },
                { label: 'Interest Payable', value: `₹ ${data.interestAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}` },
                { label: 'Equated Installment', value: `₹ ${data.installmentAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}` },
                { label: 'Total Installments', value: `${data.installmentPeriod} ${installmentTypePhrase}` },
                { label: 'Start Date', value: new Date(data.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) },
                { label: 'Total Recovered', value: `₹ ${paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#0A9E5C' },
                { label: 'Outstanding', value: `₹ ${Math.max(0, expectedTotal - paidAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#ef4444' },
              ].map(({ label, value, copyable, color }) => (
                <div key={label} style={{ padding: '11px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <p style={{ margin: '0 0 3px', fontSize: 11, color: 'var(--text-muted)' }}>{label}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</p>
                    {copyable && (
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(data.accountId.split('_')[0])}
                        style={{ color: 'var(--text-muted)', padding: 2 }}
                      >
                        <ContentCopy style={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))}

              {isNotTallied && (
                <div style={{ marginTop: 16, padding: '10px 12px', background: 'rgba(248,113,113,0.08)', borderRadius: 10, border: '1px solid rgba(248,113,113,0.2)' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#f87171', fontWeight: 600 }}>⚠️ Amount Mismatch</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                    Expected: ₹{(paidDayCount * data.installmentAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}<br />
                    Recovered: ₹{paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                  </p>
                </div>
              )}
            </Box>
          </div>

          {/* FAB — Add Entry */}
          {paidDayCount < data.installmentPeriod && (
            <AddEntryForm
            isFab={true}
            refresh={getDetails}
            accountId={data.accountId}
            installmentAmount={data.installmentAmount}
            installmentType={data.installmentType || 'daily'}
            buttonStyle={{
              position: "fixed",
              right: 20,
              bottom: 80,
              zIndex: 1000,
              boxShadow: "0 4px 14px rgba(0,0,0,0.3)"
            }}
          />
          )}
        </>
      ) : (
        <p>No data found</p>
      )}
    </div>
  );
}

export default AccountDetail;
